import { useI18n, type Lang } from "@/lib/i18n";

const EMAIL = "client@fa.kg";
const PHONE = "+996 505 273 072";

export function Privacy() {
  const { lang, setLang } = useI18n();
  const c = COPY[lang];
  return (
    <article className="mx-auto max-w-3xl px-5 py-10 leading-relaxed text-tg-text">
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-[28px] font-semibold leading-tight">{c.title}</h1>
        <LangSwitch lang={lang} setLang={setLang} />
      </div>
      <p className="mt-2 text-[13px] text-tg-hint">{c.updated}</p>
      <p className="mt-1 text-[13px] text-tg-hint">
        {c.sourceUrl}:{" "}
        <a className="font-mono text-tg-link" href="https://ttex.online/privacy">https://ttex.online/privacy</a>
      </p>

      {c.sections.map((s, i) => (
        <Section key={i} title={s.title}>
          {s.body}
        </Section>
      ))}

      <p className="mt-10 text-[12px] text-tg-hint">{c.footer}</p>
    </article>
  );
}

function LangSwitch({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  return (
    <div className="flex shrink-0 rounded-full bg-tg-bg-sec p-1 text-[12px] font-medium">
      <button
        onClick={() => setLang("ru")}
        className={`rounded-full px-2.5 py-1 ${lang === "ru" ? "bg-tg-link text-white" : "text-tg-hint"}`}
      >
        RU
      </button>
      <button
        onClick={() => setLang("en")}
        className={`rounded-full px-2.5 py-1 ${lang === "en" ? "bg-tg-link text-white" : "text-tg-hint"}`}
      >
        EN
      </button>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-[20px] font-semibold">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px]">{children}</div>
    </section>
  );
}

function List({ children }: { children: React.ReactNode }) {
  return <ul className="ml-5 list-disc space-y-2">{children}</ul>;
}

function Address({ lang }: { lang: Lang }) {
  const en = lang === "en";
  return (
    <address className="not-italic rounded-xl bg-tg-bg-sec p-4 text-[14px]">
      <div><strong>{en ? "Financial Alliance LLC" : "ОсОО «Финансовый Альянс»"}</strong></div>
      {en ? (
        <>
          <div>Razzakov St 22, Pervomaisky district</div>
          <div>Bishkek, Kyrgyz Republic</div>
          <div>TIN 01205202310463</div>
          <div className="mt-1">Securities market brokerage license, registration No. 3056101024 dated 10 October 2024</div>
        </>
      ) : (
        <>
          <div>г. Бишкек, Первомайский р-н, ул. Раззакова, д. 22</div>
          <div>Кыргызская Республика</div>
          <div>ИНН 01205202310463</div>
          <div className="mt-1">Лицензия на осуществление брокерской деятельности на рынке ценных бумаг, регистрационный номер 3056101024 от 10 октября 2024 года</div>
        </>
      )}
      <div className="mt-1">
        {en ? "Email" : "Эл. почта"}:{" "}
        <a className="text-tg-link underline" href={`mailto:${EMAIL}`}>{EMAIL}</a>
      </div>
      <div>{en ? "Phone" : "Телефон"}: {PHONE}</div>
    </address>
  );
}

type Copy = {
  title: string;
  updated: string;
  sourceUrl: string;
  footer: string;
  sections: { title: string; body: React.ReactNode }[];
};

const COPY: Record<Lang, Copy> = {
  en: {
    title: "Privacy Policy",
    updated: "Last updated: 30 July 2026 · Effective date: 30 July 2026",
    sourceUrl: "Source URL",
    footer:
      "This Privacy Policy is provided in English and Russian. In case of any inconsistency, the English version prevails.",
    sections: [
      {
        title: "1. Who we are",
        body: (
          <>
            <p>
              This Privacy Policy describes how <strong>Financial Alliance LLC</strong> ("we", "us", "our", or the
              "Company"), a company incorporated in the Kyrgyz Republic and licensed to carry out brokerage
              activity on the securities market (registration No. 3056101024 dated 10 October 2024), collects, uses,
              shares, and protects personal information when you use our service for the issuance and servicing of
              payment cards (including top-up and cash-out), operated through a Telegram Mini App and related APIs
              (the "Service").
            </p>
            <Address lang="en" />
          </>
        ),
      },
      {
        title: "2. Scope",
        body: (
          <p>
            This Policy applies to individuals who interact with the Service, including visitors to our website at
            {" "}<code className="font-mono">https://ttex.online</code> and users of the Telegram bot
            {" "}<code className="font-mono">@ttex_robot</code>. By using the Service you confirm that you have
            read and understood this Policy.
          </p>
        ),
      },
      {
        title: "3. Information we collect",
        body: (
          <>
            <p>We collect and process the following categories of personal data:</p>
            <List>
              <li>
                <strong>Telegram account data</strong> received via Telegram WebApp <em>initData</em>: numeric
                Telegram user ID, first name, last name, username, language code, premium status.
              </li>
              <li><strong>Contact details</strong>: mobile phone number and email you provide for verification and account communications.</li>
              <li>
                <strong>Identity verification data</strong> collected and processed on our behalf by Sumsub
                (Sum and Substance Ltd): government-issued identity document images (passport), facial image and
                liveness video, full name, date of birth, place of birth, document number, issuing authority,
                document issuance and expiry dates, nationality, and the verification result.
              </li>
              <li>
                <strong>Card and account data</strong>: the identifier and status of the payment card issued to
                you and your account balance. Full card numbers and security codes are handled by the card issuer
                and are not stored by us.
              </li>
              <li>
                <strong>Transaction data</strong>: top-up amount in RUB, amount credited in USD, exchange rate,
                payment method, payment reference, card top-up and cash-out references, transaction status and
                timestamps.
              </li>
              <li>
                <strong>Technical data</strong>: IP address, device and browser identifiers, request timestamps
                and server log entries created when you interact with our APIs.
              </li>
              <li><strong>Communications</strong>: messages you send to our support email or through the bot.</li>
            </List>
            <p>
              We do <strong>not</strong> intentionally collect special categories of data (race, religion, health,
              political views, etc.). Biometric data (facial image and liveness data) is processed solely for
              identity verification by Sumsub in accordance with their own privacy practices and is not retained by
              us in raw form.
            </p>
          </>
        ),
      },
      {
        title: "4. How we use your information",
        body: (
          <List>
            <li>To verify your identity and comply with KYC and Anti-Money-Laundering (AML) obligations.</li>
            <li>To create and operate your account and to issue and service your payment card.</li>
            <li>To process card top-ups and cash-outs and credit your balance.</li>
            <li>To prevent, detect and investigate fraud, abuse and security incidents.</li>
            <li>To respond to your support requests and communicate with you about the Service.</li>
            <li>To comply with applicable laws and respond to lawful requests from public authorities.</li>
            <li>To maintain records as required by financial-services and tax legislation.</li>
          </List>
        ),
      },
      {
        title: "5. Legal bases for processing",
        body: (
          <>
            <p>
              We process personal data in accordance with the Law of the Kyrgyz Republic "On personal information"
              and other applicable legislation of the Kyrgyz Republic. Where the EU/UK General Data Protection
              Regulation additionally applies, we rely on the following legal bases:
            </p>
            <List>
              <li><strong>Performance of a contract</strong> — to provide the Service you request.</li>
              <li><strong>Legal obligation</strong> — to comply with AML/CTF, sanctions and tax laws.</li>
              <li>
                <strong>Legitimate interests</strong> — to operate, secure and improve the Service, prevent fraud
                and enforce our terms, provided that such interests are not overridden by your fundamental rights.
              </li>
              <li>
                <strong>Consent</strong> — where required, for example for optional communications. You may
                withdraw consent at any time by contacting us.
              </li>
            </List>
          </>
        ),
      },
      {
        title: "6. Sharing with third parties",
        body: (
          <>
            <p>We share your personal data only with the following categories of recipients:</p>
            <List>
              <li>
                <strong>Identity verification provider — Sumsub (Sum and Substance Ltd, 30 St Mary Axe,
                London EC3A 8BF, United Kingdom)</strong>: receives identity documents, biometric and liveness data, and other
                attributes necessary to perform KYC. See
                {" "}
                <a className="text-tg-link underline" href="https://sumsub.com/privacy-notice/" target="_blank" rel="noreferrer">
                  Sumsub Privacy Notice
                </a>.
              </li>
              <li>
                <strong>Messaging platform — Telegram FZ-LLC</strong>: we communicate with you through Telegram and
                rely on Telegram's WebApp protocol to authenticate you. Telegram processes your data per its own
                privacy policy.
              </li>
              <li>
                <strong>Card-issuing partner</strong>: our partner that issues and services the payment cards
                receives the data necessary to issue your card, process top-ups and cash-outs and collect your
                payment via bank transfer to the requisites we provide.
              </li>
              <li>
                <strong>Infrastructure providers</strong>: hosting and database providers acting strictly on our
                documented instructions as data processors.
              </li>
              <li>
                <strong>Public authorities and law enforcement</strong>: where disclosure is required by law,
                regulation, court order or a binding request from a competent authority.
              </li>
              <li>
                <strong>Professional advisers</strong>: legal, tax and audit advisers under confidentiality
                obligations, on a need-to-know basis.
              </li>
            </List>
            <p>We do not sell your personal information.</p>
          </>
        ),
      },
      {
        title: "7. International data transfers",
        body: (
          <p>
            Your personal data is primarily stored and processed in the Kyrgyz Republic. Because we use service
            providers located in other countries, your data may also be transferred to and processed in
            jurisdictions other than where you reside (in particular, the European Economic Area — Lithuania).
            Where required, we put in place appropriate safeguards such as the European Commission's Standard
            Contractual Clauses or equivalent mechanisms.
          </p>
        ),
      },
      {
        title: "8. Retention",
        body: (
          <>
            <p>We keep your personal data only for as long as necessary for the purposes set out in this Policy:</p>
            <List>
              <li>
                <strong>KYC records and supporting documents</strong>: retained for at least{" "}
                <strong>5 years</strong> after the end of our business relationship, in line with international AML
                standards, or longer if required by applicable law.
              </li>
              <li><strong>Transaction records</strong>: retained for at least 5 years from the date of the transaction.</li>
              <li>
                <strong>Account data</strong>: retained while your account remains active and for a reasonable
                period thereafter to fulfil legal and operational obligations.
              </li>
              <li><strong>Server logs and technical data</strong>: typically retained for up to 12 months.</li>
            </List>
            <p>At the end of the applicable retention period we delete or irreversibly anonymise the data.</p>
          </>
        ),
      },
      {
        title: "9. Your rights",
        body: (
          <>
            <p>Subject to applicable law, you have the right to:</p>
            <List>
              <li>access the personal data we hold about you and receive a copy;</li>
              <li>request correction of inaccurate or incomplete data;</li>
              <li>request erasure of your data, subject to our legal retention obligations;</li>
              <li>request restriction of, or object to, certain processing activities;</li>
              <li>request portability of data you provided to us, where technically feasible;</li>
              <li>withdraw consent at any time where processing is based on consent;</li>
              <li>lodge a complaint with your local data-protection authority.</li>
            </List>
            <p>
              To exercise these rights, contact us at{" "}
              <a className="text-tg-link underline" href={`mailto:${EMAIL}`}>{EMAIL}</a>. We may need to verify
              your identity before fulfilling your request.
            </p>
          </>
        ),
      },
      {
        title: "10. Security",
        body: (
          <p>
            We use technical and organisational measures to protect personal data, including transport encryption
            (HTTPS/TLS), encryption of sensitive fields at rest, role-based access controls, audit logging of
            administrative actions, and segregation of production secrets from source code. No method of
            transmission or storage is completely secure; we continuously work to improve our safeguards.
          </p>
        ),
      },
      {
        title: "11. Cookies and similar technologies",
        body: (
          <p>
            The Service uses only strictly necessary cookies: a signed session cookie for our administrative panel
            (HttpOnly, Secure, SameSite=None). We do not use third-party analytics, advertising cookies, or
            tracking pixels on this site at the time of writing.
          </p>
        ),
      },
      {
        title: "12. Children",
        body: (
          <p>
            The Service is not directed to individuals under the age of 18. We do not knowingly collect personal
            data from children. If you believe we have collected such data, please contact us so we can delete it.
          </p>
        ),
      },
      {
        title: "13. Automated decision-making",
        body: (
          <p>
            Identity verification involves automated checks performed by Sumsub (document and biometric matching,
            watchlist screening). The final decision to onboard a customer and issue a card is reviewed by us. You
            have the right to request human review of an automated decision by contacting us.
          </p>
        ),
      },
      {
        title: "14. Changes to this Policy",
        body: (
          <p>
            We may update this Policy from time to time to reflect changes in our practices or applicable law. When
            we make material changes, we will update the "Last updated" date above and, where appropriate, notify
            you in the Service. Continued use of the Service after changes take effect constitutes acceptance of
            the updated Policy.
          </p>
        ),
      },
      {
        title: "15. Contact us",
        body: (
          <>
            <p>For questions about this Privacy Policy or to exercise your rights, contact:</p>
            <Address lang="en" />
          </>
        ),
      },
    ],
  },
  ru: {
    title: "Политика конфиденциальности",
    updated: "Последнее обновление: 30 июля 2026 · Дата вступления в силу: 30 июля 2026",
    sourceUrl: "Адрес страницы",
    footer:
      "Настоящая Политика конфиденциальности предоставляется на русском и английском языках. В случае расхождений преимущественную силу имеет английская версия.",
    sections: [
      {
        title: "1. Кто мы",
        body: (
          <>
            <p>
              Настоящая Политика конфиденциальности описывает, как <strong>ОсОО «Финансовый Альянс»</strong>
              («мы», «нас», «наш» или «Компания»), компания, зарегистрированная в Кыргызской Республике и имеющая
              лицензию на осуществление брокерской деятельности на рынке ценных бумаг (регистрационный номер 3056101024 от
              10 октября 2024 года), собирает, использует, передаёт и защищает персональные данные при
              использовании вами нашего сервиса по выпуску и обслуживанию платёжных карт (включая пополнение и
              вывод средств), работающего через Telegram Mini App и связанные API («Сервис»).
            </p>
            <Address lang="ru" />
          </>
        ),
      },
      {
        title: "2. Сфера применения",
        body: (
          <p>
            Настоящая Политика применяется к физическим лицам, взаимодействующим с Сервисом, включая посетителей
            нашего сайта {" "}<code className="font-mono">https://ttex.online</code> и пользователей
            Telegram-бота {" "}<code className="font-mono">@ttex_robot</code>. Используя Сервис, вы
            подтверждаете, что ознакомились с настоящей Политикой и поняли её.
          </p>
        ),
      },
      {
        title: "3. Какие данные мы собираем",
        body: (
          <>
            <p>Мы собираем и обрабатываем следующие категории персональных данных:</p>
            <List>
              <li>
                <strong>Данные аккаунта Telegram</strong>, получаемые через Telegram WebApp <em>initData</em>:
                числовой идентификатор пользователя Telegram, имя, фамилия, username, код языка, статус premium.
              </li>
              <li><strong>Контактные данные</strong>: номер мобильного телефона и адрес эл. почты, которые вы предоставляете для верификации и связи по аккаунту.</li>
              <li>
                <strong>Данные для верификации личности</strong>, собираемые и обрабатываемые от нашего имени
                провайдером Sumsub (Sum and Substance Ltd): изображения удостоверяющего личность документа
                (паспорт), фотография лица и видео liveness, ФИО, дата рождения, место рождения, номер документа,
                орган выдачи, даты выдачи и окончания срока действия документа, гражданство и результат проверки.
              </li>
              <li>
                <strong>Данные карты и аккаунта</strong>: идентификатор и статус выпущенной вам платёжной карты и
                баланс вашего счёта. Полные номера карт и коды безопасности обрабатываются эмитентом карты и нами
                не хранятся.
              </li>
              <li>
                <strong>Данные о транзакциях</strong>: сумма пополнения в RUB, зачисленная сумма в USD, курс
                обмена, способ оплаты, референс платежа, референсы пополнения и вывода по карте, статус транзакции
                и отметки времени.
              </li>
              <li>
                <strong>Технические данные</strong>: IP-адрес, идентификаторы устройства и браузера, отметки
                времени запросов и записи серверных журналов, создаваемые при взаимодействии с нашими API.
              </li>
              <li><strong>Переписка</strong>: сообщения, которые вы направляете на нашу почту поддержки или через бота.</li>
            </List>
            <p>
              Мы <strong>не</strong> собираем намеренно специальные категории данных (раса, религия, здоровье,
              политические взгляды и т. п.). Биометрические данные (изображение лица и данные liveness)
              обрабатываются исключительно для верификации личности провайдером Sumsub в соответствии с его
              собственными практиками конфиденциальности и не хранятся нами в исходном виде.
            </p>
          </>
        ),
      },
      {
        title: "4. Как мы используем ваши данные",
        body: (
          <List>
            <li>Для верификации вашей личности и соблюдения обязательств KYC и по противодействию отмыванию денег (AML).</li>
            <li>Для создания и функционирования вашего аккаунта, а также выпуска и обслуживания платёжной карты.</li>
            <li>Для обработки пополнений и выводов по карте и зачисления средств на баланс.</li>
            <li>Для предотвращения, выявления и расследования мошенничества, злоупотреблений и инцидентов безопасности.</li>
            <li>Для ответа на ваши обращения в поддержку и связи с вами по вопросам Сервиса.</li>
            <li>Для соблюдения применимого законодательства и ответа на законные запросы государственных органов.</li>
            <li>Для ведения учёта в соответствии с законодательством о финансовых услугах и налогах.</li>
          </List>
        ),
      },
      {
        title: "5. Правовые основания обработки",
        body: (
          <>
            <p>
              Мы обрабатываем персональные данные в соответствии с Законом Кыргызской Республики «Об информации
              персонального характера» и иным применимым законодательством Кыргызской Республики. Если
              дополнительно применяется Общий регламент по защите данных ЕС/Великобритании (GDPR), мы опираемся на
              следующие правовые основания:
            </p>
            <List>
              <li><strong>Исполнение договора</strong> — для предоставления запрошенного вами Сервиса.</li>
              <li><strong>Юридическая обязанность</strong> — для соблюдения законов AML/CTF, санкционного и налогового законодательства.</li>
              <li>
                <strong>Законные интересы</strong> — для работы, защиты и улучшения Сервиса, предотвращения
                мошенничества и обеспечения соблюдения наших условий, при условии что такие интересы не
                перевешиваются вашими основными правами.
              </li>
              <li>
                <strong>Согласие</strong> — где это требуется, например для необязательных рассылок. Вы можете
                отозвать согласие в любое время, связавшись с нами.
              </li>
            </List>
          </>
        ),
      },
      {
        title: "6. Передача третьим лицам",
        body: (
          <>
            <p>Мы передаём ваши персональные данные только следующим категориям получателей:</p>
            <List>
              <li>
                <strong>Провайдер верификации личности — Sumsub (Sum and Substance Ltd, 30 St Mary Axe,
                London EC3A 8BF, United Kingdom)</strong>: получает документы, удостоверяющие личность, биометрические данные и
                данные liveness, а также иные атрибуты, необходимые для проведения KYC. См.
                {" "}
                <a className="text-tg-link underline" href="https://sumsub.com/privacy-notice/" target="_blank" rel="noreferrer">
                  Политику конфиденциальности Sumsub
                </a>.
              </li>
              <li>
                <strong>Мессенджер — Telegram FZ-LLC</strong>: мы общаемся с вами через Telegram и используем
                протокол Telegram WebApp для вашей аутентификации. Telegram обрабатывает ваши данные согласно
                собственной политике конфиденциальности.
              </li>
              <li>
                <strong>Партнёр-эмитент карт</strong>: наш партнёр, выпускающий и обслуживающий платёжные карты,
                получает данные, необходимые для выпуска вашей карты, обработки пополнений и выводов, а также
                приёма вашего платежа банковским переводом на предоставляемые нами реквизиты.
              </li>
              <li>
                <strong>Поставщики инфраструктуры</strong>: провайдеры хостинга и баз данных, действующие строго
                по нашим документированным инструкциям в качестве обработчиков данных.
              </li>
              <li>
                <strong>Государственные органы и правоохранительные органы</strong>: когда раскрытие требуется по
                закону, нормативному акту, решению суда или обязательному запросу компетентного органа.
              </li>
              <li>
                <strong>Профессиональные консультанты</strong>: юридические, налоговые и аудиторские консультанты
                под обязательствами конфиденциальности, по принципу необходимого знания.
              </li>
            </List>
            <p>Мы не продаём ваши персональные данные.</p>
          </>
        ),
      },
      {
        title: "7. Трансграничная передача данных",
        body: (
          <p>
            Ваши персональные данные преимущественно хранятся и обрабатываются в Кыргызской Республике. Поскольку
            мы используем поставщиков услуг, расположенных в других странах, ваши данные также могут передаваться
            и обрабатываться в юрисдикциях, отличных от страны вашего проживания (в частности, в Европейской
            экономической зоне — Литве). Где это требуется, мы применяем надлежащие меры защиты, такие как
            Стандартные договорные положения Европейской комиссии или эквивалентные механизмы.
          </p>
        ),
      },
      {
        title: "8. Сроки хранения",
        body: (
          <>
            <p>Мы храним ваши персональные данные только столько, сколько необходимо для целей настоящей Политики:</p>
            <List>
              <li>
                <strong>Записи KYC и подтверждающие документы</strong>: хранятся не менее{" "}
                <strong>5 лет</strong> после окончания наших деловых отношений в соответствии с международными
                стандартами AML или дольше, если это требуется применимым законодательством.
              </li>
              <li><strong>Записи о транзакциях</strong>: хранятся не менее 5 лет с даты транзакции.</li>
              <li>
                <strong>Данные аккаунта</strong>: хранятся, пока ваш аккаунт активен, и в течение разумного
                периода после этого для выполнения юридических и операционных обязательств.
              </li>
              <li><strong>Серверные журналы и технические данные</strong>: обычно хранятся до 12 месяцев.</li>
            </List>
            <p>По окончании применимого срока хранения мы удаляем или необратимо обезличиваем данные.</p>
          </>
        ),
      },
      {
        title: "9. Ваши права",
        body: (
          <>
            <p>С учётом применимого законодательства вы имеете право:</p>
            <List>
              <li>получить доступ к персональным данным, которые мы храним о вас, и получить их копию;</li>
              <li>требовать исправления неточных или неполных данных;</li>
              <li>требовать удаления ваших данных с учётом наших юридических обязательств по хранению;</li>
              <li>требовать ограничения обработки или возражать против определённых операций обработки;</li>
              <li>требовать переносимости предоставленных вами данных, где это технически возможно;</li>
              <li>отозвать согласие в любое время, если обработка основана на согласии;</li>
              <li>подать жалобу в местный орган по защите персональных данных.</li>
            </List>
            <p>
              Для реализации этих прав свяжитесь с нами по адресу{" "}
              <a className="text-tg-link underline" href={`mailto:${EMAIL}`}>{EMAIL}</a>. Возможно, нам
              потребуется подтвердить вашу личность перед выполнением запроса.
            </p>
          </>
        ),
      },
      {
        title: "10. Безопасность",
        body: (
          <p>
            Мы применяем технические и организационные меры для защиты персональных данных, включая шифрование при
            передаче (HTTPS/TLS), шифрование чувствительных полей при хранении, ролевую модель доступа, аудит
            административных действий и отделение производственных секретов от исходного кода. Ни один метод
            передачи или хранения не является полностью безопасным; мы постоянно работаем над улучшением защиты.
          </p>
        ),
      },
      {
        title: "11. Файлы cookie и аналогичные технологии",
        body: (
          <p>
            Сервис использует только строго необходимые файлы cookie: подписанный сессионный cookie для нашей
            административной панели (HttpOnly, Secure, SameSite=None). На данном сайте мы не используем сторонние
            аналитические, рекламные cookie или трекинговые пиксели на момент публикации.
          </p>
        ),
      },
      {
        title: "12. Дети",
        body: (
          <p>
            Сервис не предназначен для лиц младше 18 лет. Мы сознательно не собираем персональные данные детей.
            Если вы полагаете, что мы получили такие данные, свяжитесь с нами, чтобы мы могли их удалить.
          </p>
        ),
      },
      {
        title: "13. Автоматизированное принятие решений",
        body: (
          <p>
            Верификация личности включает автоматические проверки, выполняемые Sumsub (сопоставление документов и
            биометрии, проверка по спискам). Окончательное решение о принятии клиента и выпуске карты
            пересматривается нами. Вы вправе запросить проверку автоматизированного решения человеком, связавшись
            с нами.
          </p>
        ),
      },
      {
        title: "14. Изменения настоящей Политики",
        body: (
          <p>
            Мы можем время от времени обновлять настоящую Политику, отражая изменения в наших практиках или
            применимом законодательстве. При внесении существенных изменений мы обновим дату «Последнее
            обновление» выше и, при необходимости, уведомим вас в Сервисе. Продолжение использования Сервиса после
            вступления изменений в силу означает принятие обновлённой Политики.
          </p>
        ),
      },
      {
        title: "15. Связаться с нами",
        body: (
          <>
            <p>По вопросам о настоящей Политике конфиденциальности или для реализации ваших прав обращайтесь:</p>
            <Address lang="ru" />
          </>
        ),
      },
    ],
  },
};
